import React, { useState, useEffect } from 'react';
import { Layout, Menu, Space, Dropdown, Avatar, Tag } from 'antd';
import { StarTwoTone, SafetyCertificateTwoTone } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined, 
  LineChartOutlined, 
  TeamOutlined,
  LogoutOutlined,
  DashboardOutlined,
  ToolOutlined,
  ScanOutlined,
  ProjectTwoTone,
  BellOutlined
} from '@ant-design/icons';

const { Header, Sider, Content } = Layout;

interface PersonalShellProps {
  children: React.ReactNode;
  memberStatus: 'premium' | 'admin';
  onLogout: () => void;
  userEmail: string;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export const PersonalShell: React.FC<PersonalShellProps> = ({
  children,
  memberStatus,
  onLogout,
  userEmail
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 991px)');
  const [collapsed, setCollapsed] = useState(isMobile);

  useEffect(() => {
    if (isMobile) setCollapsed(true);
  }, [isMobile]);

  const menuItems = [
    {
      key: '/personal/dashboard',
      icon: <DashboardOutlined />,
      label: 'Beranda',
    },
    {
      key: '/personal/chart',
      icon: <LineChartOutlined />,
      label: 'Bagan BaZi Kamu',
    },
    {
      key: '/personal/synergy',
      icon: <TeamOutlined />,
      label: 'Cek Kecocokan',
    },
    ...(memberStatus === 'admin' ? [
      {
        key: '/personal/synergy',
        icon: <TeamOutlined />,
        label: 'Cek Kecocokan',
      },
      {
        key: '/personal/admin/free-form',
        icon: <ToolOutlined />,
        label: 'Admin Free Form',
      },
      {
        key: '/personal/admin/qris',
        icon: <ScanOutlined />,
        label: 'Admin QRIS',
      },
      {
        key: '/personal/admin/payments',
        icon: <ToolOutlined />,
        label: 'Admin Payments',
      },
      {
        key: '/personal/admin/notifications',
        icon: <BellOutlined />,
        label: 'Notifikasi Harian',
      },
    ] : []),
  ];

  const userMenu = {
    items: [
      {
        key: 'email',
        label: <span style={{ fontWeight: 600 }}>{userEmail}</span>,
        disabled: true,
      },
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: 'Profil Saya',
        onClick: () => navigate('/personal/profile'),
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Keluar',
        onClick: onLogout,
      },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Mobile backdrop */}
      {isMobile && !collapsed && (
        <div
          onClick={() => setCollapsed(true)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 99,
            transition: 'opacity 0.3s',
          }}
        />
      )}

      <Header style={{ 
        background: '#ffffff', 
        padding: '0 16px',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '3px solid #1a202c',
        height: '64px',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Hamburger - visible on mobile */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: 'none',
              border: '2px solid #1a202c',
              borderRadius: '8px',
              padding: '6px 8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: '#1a202c',
              boxShadow: '1px 1px 0px 0px #1a202c',
            }}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>

          <span style={{ 
            fontSize: 'clamp(1rem, 4vw, 1.4rem)',
            fontWeight: 900, 
            color: '#1a202c',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
          }}>
            <ProjectTwoTone style={{ marginRight: 6 }} /> BaZi <span style={{ display: 'inline-block' }}>Insights</span>
            <span style={{ 
              fontSize: 'clamp(0.6rem, 2vw, 0.8rem)',
              color: '#1098ad', 
              verticalAlign: 'super', 
              fontWeight: 800,
              marginLeft: '2px',
            }}>PERSONAL</span>
          </span>
        </div>

        <Space size="small" style={{ flexShrink: 0 }}>
          {!isMobile && (memberStatus === 'admin' ? (
            <Tag color="red" style={{ 
              fontWeight: 800, 
              fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)',
              padding: '2px 10px',
              borderRadius: '20px',
              border: '2px solid #c92a2a',
              background: '#fff5f5',
              marginRight: 0,
              whiteSpace: 'nowrap',
            }}>
              <><SafetyCertificateTwoTone twoToneColor="#f5222d" style={{ marginRight: 4 }} /> Admin</>
            </Tag>
          ) : (
            <Tag color="gold" style={{ 
              fontWeight: 800, 
              fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)',
              padding: '2px 10px',
              borderRadius: '20px',
              border: '2px solid #d48806',
              marginRight: 0,
              whiteSpace: 'nowrap',
            }}>
              <><StarTwoTone twoToneColor="#fadb14" style={{ marginRight: 4 }} /> Premium</>
            </Tag>
          ))}

          <Dropdown menu={userMenu} placement="bottomRight" arrow>
            <Avatar 
              icon={<UserOutlined />} 
              style={{ 
                backgroundColor: '#1098ad', 
                border: '2px solid #1a202c',
                cursor: 'pointer',
                boxShadow: '1px 1px 0px 0px #1a202c',
              }} 
            />
          </Dropdown>
        </Space>
      </Header>

      <Layout style={{ position: 'relative', paddingTop: '64px', minHeight: 'calc(100vh - 64px)', background: '#f8f9fa' }}>
        <Sider 
          width={240}
          collapsedWidth={isMobile ? 0 : 80}
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ 
            background: '#f8f9fa',
            paddingTop: '12px',
            position: isMobile ? 'fixed' : 'relative',
            top: isMobile ? '64px' : 0,
            left: 0,
            bottom: isMobile ? 0 : undefined,
            zIndex: isMobile ? 100 : undefined,
            overflowY: 'auto',
            overflowX: 'hidden',
            borderRight: '1px solid #e9ecef',
            transition: 'all 0.25s ease',
          }}
        >
          <style>{`
            .sidebar-menu.ant-menu { border-inline-end: none !important; background: transparent !important; }
            .sidebar-menu.ant-menu-inline .ant-menu-item { border-radius: 0 !important; margin: 0 !important; width: 100% !important; }
            .sidebar-menu.ant-menu-inline .ant-menu-item-selected { background: #fff9db !important; }
            .sidebar-menu.ant-menu-inline .ant-menu-item::after { border-right: 3px solid #fab005 !important; }
          `}</style>
          <Menu
            className="sidebar-menu"
            mode="inline"
            selectedKeys={[location.pathname]}
            style={{ borderRight: 0, borderBottom: 0, fontWeight: 700, background: 'transparent' }}
            items={menuItems}
            onClick={({ key }) => {
              navigate(key);
              if (isMobile) setCollapsed(true);
            }}
          />
        </Sider>

        <Content style={{ 
          padding: 'clamp(12px, 3vw, 24px)',
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 240),
          minHeight: 'calc(100vh - 64px)',
          background: '#f8f9fa',
          overflowY: 'auto',
          transition: 'margin-left 0.25s ease',
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};
